// 类型定义
type ValidatorDecorator = (target: any, propertyKey: string) => void;

interface ValidationRule {
  type: string;
  message: string;
  value?: any;
  validator?: (value: any) => boolean;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  fieldErrors: Record<string, string[]>;
}

// 自定义校验错误类
export class ValidationError extends Error {
  public fieldErrors: Record<string, string[]>;
  public errors: string[];

  constructor(message: string) {
    super(message);
    this.name = "ValidationError";
  }
}

// 存储校验元数据
const validationMetadata = new WeakMap<any, Map<string, ValidationRule[]>>();

// 添加校验规则的辅助函数
function addValidationRule(
  target: any,
  propertyKey: string,
  rule: ValidationRule
) {
  if (!validationMetadata.has(target)) {
    validationMetadata.set(target, new Map());
  }
  const classMetadata = validationMetadata.get(target)!;
  if (!classMetadata.has(propertyKey)) {
    classMetadata.set(propertyKey, []);
  }
  classMetadata.get(propertyKey)!.push(rule);
}

// 装饰器函数
export function Required(message?: string): ValidatorDecorator {
  return function (target: any, propertyKey: string) {
    addValidationRule(target.constructor, propertyKey, {
      type: "required",
      message: message || `${propertyKey} required`,
    });
  };
}

export function IsString(message?: string): ValidatorDecorator {
  return function (target: any, propertyKey: string) {
    addValidationRule(target.constructor, propertyKey, {
      type: "string",
      message: message || `${propertyKey} must be string`,
    });
  };
}

export function IsNumber(message?: string): ValidatorDecorator {
  return function (target: any, propertyKey: string) {
    addValidationRule(target.constructor, propertyKey, {
      type: "number",
      message: message || `${propertyKey} must be number`,
    });
  };
}

export function IsBoolean(message?: string): ValidatorDecorator {
  return function (target: any, propertyKey: string) {
    addValidationRule(target.constructor, propertyKey, {
      type: "boolean",
      message: message || `${propertyKey} must be boolean`,
    });
  };
}

export function IsArray(message?: string): ValidatorDecorator {
  return function (target: any, propertyKey: string) {
    addValidationRule(target.constructor, propertyKey, {
      type: "array",
      message: message || `${propertyKey} must be array`,
    });
  };
}

export function IsEmail(message?: string): ValidatorDecorator {
  return function (target: any, propertyKey: string) {
    addValidationRule(target.constructor, propertyKey, {
      type: "email",
      message: message || `${propertyKey} must be email`,
    });
  };
}

export function IsStringOrNumber(message?: string): ValidatorDecorator {
  return function (target: any, propertyKey: string) {
    addValidationRule(target.constructor, propertyKey, {
      type: "custom",
      message: message || `${propertyKey} must be string or number`,
      validator: (value) => {
        if (value === undefined || value === null) return true;
        return typeof value === "string" || (typeof value === "number" && !isNaN(value));
      },
    });
  };
}

export function ValidateNested(validationClass: new () => any, message?: string): ValidatorDecorator {
  return function (target: any, propertyKey: string) {
    addValidationRule(target.constructor, propertyKey, {
      type: "nested",
      message: message || `${propertyKey} nested validation failed`,
      value: validationClass,
    });
  };
}

// 数据校验器类
export class DataValidator {
  /**
   * 校验数据对象（返回结果）
   * @param data 要校验的数据
   * @param validationClass 校验类
   * @returns 校验结果
   */
  static validate<T>(
    data: any,
    validationClass: new () => T
  ): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      fieldErrors: {},
    };

    const metadata = validationMetadata.get(validationClass);

    if (!metadata) {
      return result;
    }

    for (const [propertyKey, rules] of metadata) {
      const value = data[propertyKey];
      const fieldErrors: string[] = [];

      for (const rule of rules) {
        const error = this.validateRule(value, rule);
        if (error) {
          fieldErrors.push(error);
          result.errors.push(error);
          result.isValid = false;
        }
      }

      if (fieldErrors.length > 0) {
        result.fieldErrors[propertyKey] = fieldErrors;
      }
    }
    return result;
  }

  /**
   * 校验数据对象（失败时抛出异常）
   * @param data 要校验的数据
   * @param validationClass 校验类
   * @returns 校验通过的数据
   * @throws ValidationError 校验失败时抛出
   */
  static validateOrThrow<T>(data: any, validationClass: new () => T): T {
    const result = this.validate(data, validationClass);

    if (!result.isValid) {
      // 每次仅显示第一条校验失败的消息
      throw new ValidationError(`${result.errors[0]}`);
    }

    return data as T;
  }

  /**
   * 校验单个规则
   */
  private static validateRule(value: any, rule: ValidationRule): string | null {
    switch (rule.type) {
      case "required":
        if (value === undefined || value === null || value === "") {
          return rule.message;
        }
        break;

      case "string":
        if (
          value !== undefined &&
          value !== null &&
          typeof value !== "string"
        ) {
          return rule.message;
        }
        break;

      case "number":
        if (
          value !== undefined &&
          value !== null &&
          (typeof value !== "number" || isNaN(value))
        ) {
          return rule.message;
        }
        break;

      case "boolean":
        if (
          value !== undefined &&
          value !== null &&
          typeof value !== "boolean"
        ) {
          return rule.message;
        }
        break;

      case "array":
        if (value !== undefined && value !== null && !Array.isArray(value)) {
          return rule.message;
        }
        break;

      case "email":
        if (value && typeof value === "string") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            return rule.message;
          }
        }
        break;

      case "length":
        if (value && (typeof value === "string" || Array.isArray(value))) {
          const { min, max } = rule.value;
          if (value.length < min || (max && value.length > max)) {
            return rule.message;
          }
        }
        break;

      case "range":
        if (
          value !== undefined &&
          value !== null &&
          typeof value === "number"
        ) {
          const { min, max } = rule.value;
          if (value < min || value > max) {
            return rule.message;
          }
        }
        break;

      case "pattern":
        if (value && typeof value === "string") {
          if (!rule.value.test(value)) {
            return rule.message;
          }
        }
        break;

      case "custom":
        if (rule.validator && !rule.validator(value)) {
          return rule.message;
        }
        break;
    
      case "nested":
        if (value && typeof value === "object") {
          if (Array.isArray(value)) {
            // 处理对象数组
            for (let i = 0; i < value.length; i++) {
              const nestedResult = this.validate(value[i], rule.value);
              if (!nestedResult.isValid) {
                return `${rule.message}[${i}]: ${nestedResult.errors[0]}`;
              }
            }
          } else {
            // 处理单个对象
            const nestedResult = this.validate(value, rule.value);
            if (!nestedResult.isValid) {
              return `${rule.message}: ${nestedResult.errors[0]}`;
            }
          }
        }
        break;
    }
    return null;
  }
}

// 下划线转驼峰的通用函数
export function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// 对象键名转换函数
export function transformKeys(obj: any): any {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(transformKeys);
  }

  const transformed: any = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = toCamelCase(key);
    transformed[camelKey] = transformKeys(value);
  }

  return transformed;
}

// 工具函数：创建校验函数（抛出异常）
export function createValidatorOrThrow<T>(validationClass: new () => T) {
  return (data: any) => DataValidator.validateOrThrow(data, validationClass);
}
